import { FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Modal from 'components/layouts/modal';
import TextArea from 'components/core/text-area';
import useApi from 'utils/use-api';
import wrapClick from 'utils/wrap-click';

interface RequestMoreInfoModalProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  requestId: number;
  onSuccess: () => void;
}

const validationSchema = Yup.object({
  message: Yup.string().required('Message is required').min(10, 'Message must be at least 10 characters'),
});

const RequestMoreInfoModal: FC<RequestMoreInfoModalProps> = ({ open, setOpen, requestId, onSuccess }) => {
  const api = useApi();

  const form = useFormik({
    initialValues: {
      message: '',
    },
    validationSchema,
    onSubmit: async values => {
      await requestMoreInfoMutation.mutateAsync(values);
    },
  });

  const requestMoreInfoMutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      const response = await api.post(`/access-requests/${requestId}/request-more-info`, {
        message: values.message,
      });
      return response.data;
    },
    onSuccess: () => {
      form.resetForm();
      onSuccess();
    },
  });

  const renderActions = () => {
    return (
      <button
        type="button"
        onClick={wrapClick(() => form.handleSubmit())}
        disabled={requestMoreInfoMutation.isPending}
        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {requestMoreInfoMutation.isPending ? 'Sending...' : 'Send Request'}
      </button>
    );
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Request More Information"
      description="Ask the requester for additional details about this request"
      size="4xl"
      renderActions={renderActions}
    >
      <div className="space-y-6 mt-4">
        <div>
          <TextArea
            id="message"
            label="Your Message"
            placeholder="Please provide more information about..."
            required={true}
            rows={5}
            {...form}
          />
          <p className="mt-2 text-sm text-gray-500">
            The requester will receive an email with your message and the request status will be updated to "More Info Needed".
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default RequestMoreInfoModal;
